/**
 * Lightweight reusable skeleton primitives for dashboard loading states.
 * No external dependency — pure Tailwind pulse animation with brand
 * tokens. Wrapping data-fetching components in these avoids the flash
 * of empty content that the June 2026 audit flagged on slow networks.
 */
export function SkeletonLine({ className = "", width = "w-full" }) {
  return (
    <div
      className={`h-3 rounded bg-slate-200/70 animate-pulse ${width} ${className}`}
      data-testid="skeleton-line"
    />
  );
}

export function SkeletonBlock({ className = "" }) {
  return (
    <div
      className={`rounded-md bg-slate-200/60 animate-pulse ${className}`}
      data-testid="skeleton-block"
    />
  );
}

export function SkeletonCard({ lines = 3, className = "" }) {
  return (
    <div
      className={`bg-white border border-slate-200 rounded-lg p-5 ${className}`}
      data-testid="skeleton-card"
    >
      <SkeletonLine width="w-24" className="mb-4" />
      <SkeletonLine className="mb-2" />
      {Array.from({ length: Math.max(0, lines - 1) }).map((_, i) => (
        <SkeletonLine key={i} className="mb-2" width={i % 2 === 0 ? "w-5/6" : "w-2/3"} />
      ))}
    </div>
  );
}

export function DashboardSkeleton() {
  return (
    <div className="space-y-8" data-testid="dashboard-skeleton">
      {/* Hero */}
      <div>
        <SkeletonLine width="w-40" className="mb-4" />
        <SkeletonBlock className="h-10 w-72 mb-3" />
        <SkeletonLine width="w-96" />
      </div>
      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <SkeletonCard key={i} lines={2} />
        ))}
      </div>
      {/* Table shell */}
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <SkeletonLine width="w-32" className="mb-5" />
        <div className="space-y-3">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-4">
              <SkeletonBlock className="h-10 w-10 rounded-full" />
              <SkeletonLine width="w-1/3" />
              <SkeletonLine width="w-1/4" />
              <SkeletonLine width="w-24" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
